import { Workbook, Worksheet } from 'exceljs';

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export interface JsonToSheetOpts {
  header?: string[];
  skipHeader?: boolean;
}

export function jsonRowsToAoA(data: any[], opts?: JsonToSheetOpts): any[][] {
  if (!Array.isArray(data) || data.length === 0) {
    const headers = opts?.header || [];
    return opts?.skipHeader || headers.length === 0 ? [] : [headers];
  }
  const headers = opts?.header || Object.keys(data[0] ?? {});
  const body = data.map((row) => headers.map((key) => (row == null ? '' : row[key])));
  if (opts?.skipHeader) {
    return body;
  }
  return [headers, ...body];
}

function addAoAToSheet(sheet: Worksheet, aoa: any[][]): void {
  aoa.forEach((row) => {
    sheet.addRow(row ?? []);
  });
}

export async function workbookFromSheets(
  sheets: Array<{ name: string; aoa: any[][] }>
): Promise<Workbook> {
  const workbook = new Workbook();
  sheets.forEach((entry) => {
    const name = (entry.name || 'Sheet1').substring(0, 31) || 'Sheet1';
    const sheet = workbook.addWorksheet(name);
    addAoAToSheet(sheet, entry.aoa || []);
  });
  return workbook;
}

export async function writeXlsxBuffer(
  sheets: Array<{ name: string; aoa: any[][] }>
): Promise<ArrayBuffer> {
  const workbook = await workbookFromSheets(sheets);
  const buffer = await workbook.xlsx.writeBuffer();
  return buffer as ArrayBuffer;
}

export function downloadXlsxBuffer(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], { type: XLSX_MIME });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export async function jsonToXlsxFile(
  sheets: Array<{ name: string; data: any[]; opts?: JsonToSheetOpts }>,
  fileName: string
): Promise<File> {
  const buffer = await writeXlsxBuffer(
    sheets.map((sheet) => ({
      name: sheet.name,
      aoa: jsonRowsToAoA(sheet.data, sheet.opts),
    }))
  );
  return new File([buffer], fileName, { type: XLSX_MIME });
}

export async function aoaToXlsxFile(
  aoa: any[][],
  fileName: string,
  sheetName = 'Sheet1'
): Promise<File> {
  const buffer = await writeXlsxBuffer([{ name: sheetName, aoa }]);
  return new File([buffer], fileName, { type: XLSX_MIME });
}
